import { useMemo } from 'react';

import { canFollowUpRun } from '@/modules/campaigns/util/campaignFollowUp';

import { useContactActivities } from './useContactActivities';
import { useContactCampaigns } from './useContactCampaigns';

// Activity ids whose "next: in X" follow-up projection is still live.
export const useLiveFollowUpProjections = (contactId: string): Set<string> => {
  const { activities } = useContactActivities(contactId);
  const { subscribers } = useContactCampaigns(contactId);

  return useMemo(() => {
    const statusByCampaign = new Map(
      subscribers.map((sub) => [
        sub.campaign.campaign_id,
        { campaignStatus: sub.campaign.status, lifecycleStatus: sub.lifecycle_status },
      ])
    );
    const latestPerCampaign = new Map<string, { id: string; ts: number }>();
    for (const activity of activities) {
      if (activity.type !== 'nba_evaluation') {
        continue;
      }
      const key = activity.links?.campaign_id ?? activity.activity_id;
      const ts = new Date(activity.timestamp).getTime();
      const current = latestPerCampaign.get(key);
      if (!current || ts > current.ts) {
        latestPerCampaign.set(key, { id: activity.activity_id, ts });
      }
    }
    const ids = new Set<string>();
    for (const [key, { id }] of latestPerCampaign) {
      const campaign = statusByCampaign.get(key);
      // An unmatched campaign stays gated off — only known, active campaigns project.
      if (campaign && canFollowUpRun(campaign.campaignStatus, campaign.lifecycleStatus)) {
        ids.add(id);
      }
    }
    return ids;
  }, [activities, subscribers]);
};
